import { CloseIcon } from '@braskai/icons';
import { Button, IconButton, Link, Modal, ModalContent, ModalWrapper } from '@braskai/ui-kit';
import { useNavigate } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';

import { FeaturedError } from '@/shared/assets/icons/FeaturedError';
import { FeaturedIcon } from '@/shared/assets/icons/FeaturedIcon';
import { FeaturedIconShield } from '@/shared/assets/icons/FeaturedIconShield';
import { PATHS } from '@/shared/constants/routes/paths';
import { useCustomerPortal } from '@/shared/hooks/useCustomerPortal';
import { useInterfaceLanguage } from '@/shared/i18n';
import { readPaidRecordingCheckoutReturn } from '@/shared/lib/recordingCheckoutReturn';

import { useAfterPurchaseModalStore } from './hooks/useAfterPurchaseModalStore';

const AfterPurchaseModalComponent = () => {
  const {
    buttonText: customButtonText,
    close,
    description,
    invoiceUrl,
    isError,
    isPending,
    showInvoiceLink,
  } = useAfterPurchaseModalStore();
  const { isCustomerPortalLoading, openCustomerPortal } = useCustomerPortal();
  const { t } = useInterfaceLanguage();
  const navigate = useNavigate();

  const handleViewInvoice = async () => {
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank');
      return;
    }

    if (isError) {
      close();
      navigate(`/${PATHS.account}`, { replace: true });
      await openCustomerPortal({ navigateTo: 'portal' });
      return;
    }

    await openCustomerPortal({ navigateTo: 'portal' });
  };

  const handleClose = () => {
    close();
    if (isError) {
      navigate(`/${PATHS.pricing}`, { replace: true, state: { pricingEntryPoint: 'after_purchase_view_plans' } });
      return;
    }

    // A purchase made from a recording paywall goes back to that recording, which repeats the blocked action.
    const paidRecordingPath = isPending ? null : readPaidRecordingCheckoutReturn()?.path;

    navigate(paidRecordingPath ?? `/${PATHS.account}`, { replace: true });
  };

  const buttonText = customButtonText || t('billing.result.viewInvoice');

  const content = isPending ? (
    <Stack alignItems="center" gap={28} paddingBottom={32} textAlign="center">
      <FeaturedIconShield height={112} width={112} />
      <Stack alignItems="center" gap={16}>
        <Typography color="grey.900" variant="title-4-md">
          {t('billing.result.processing')}
        </Typography>
        <Typography color="grey.500" sx={{ maxWidth: 440 }} textAlign="center" variant="body-2">
          {t('billing.result.processingDescription')}
        </Typography>
        <Typography color="grey.500" sx={{ maxWidth: 440 }} textAlign="center" variant="body-2">
          {t('billing.result.canClose')}
        </Typography>
      </Stack>
      <Button data-testid="after-purchase-modal-close-window" size="lg" fullWidth onClick={handleClose}>
        {t('billing.result.closeWindow')}
      </Button>
    </Stack>
  ) : (
    <Stack alignItems="center" gap={20} paddingBottom={48} textAlign="center">
      {isError ? <FeaturedError height={56} width={56} /> : <FeaturedIcon height={56} width={56} />}
      <Stack alignItems="center" gap={8}>
        <Typography color="grey.500" sx={{ maxWidth: 250 }} textAlign="center" variant="body-2">
          {description}
        </Typography>

        {(showInvoiceLink || isError) && (
          <Link
            sx={{
              cursor: 'pointer',
            }}
            data-testid="after-purchase-modal-view-invoice"
            disabled={isCustomerPortalLoading}
            size="md"
            onClick={handleViewInvoice}
          >
            {buttonText}
          </Link>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Modal size="xs" open onClose={handleClose}>
      <ModalContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <IconButton data-testid="after-purchase-modal-close" disableFixSizeIcon onClick={handleClose}>
            <CloseIcon height={24} width={24} />
          </IconButton>
        </Box>
        {content}
      </ModalContent>
    </Modal>
  );
};

export const AfterPurchaseModal = () => (
  <ModalWrapper hook={useAfterPurchaseModalStore}>
    <AfterPurchaseModalComponent />
  </ModalWrapper>
);
